<template lang="pug">
.pdp-instructions.row.row-align-flex-start.justify-flex-start
  template(v-if="!loading && items.length")
    .column.cd-6.ct-12.cm-12(v-for="(item, idx) in items" :key="`${item.sku}-${idx}`")
      .media-wrap.mt-m
        // If a thumbnail is available, use it as the link content
        RogueButton.link.thumb-link(
          v-if="item.thumbSrc && item.primary"
          color="gray"
          width="full"
          :link="rogueBuilder ? '' : item.primary.Url"
          :newTab="!rogueBuilder"
          :stop="!rogueBuilder"
          @contextmenu="onContextMenu"
        )
          StaticImage.thumb(:src="item.thumbSrc" :alt="item.primary.Name" :width="item.thumbWidth" :height="item.thumbHeight" :eager="true" :contain="true" :style="item.thumbStyle")
        // Otherwise, fall back to a text link
        RogueButton.link(
          v-else-if="item.primary"
          color="gray"
          width="full"
          :link="rogueBuilder ? '' : item.primary.Url"
          :newTab="!rogueBuilder"
          :stop="!rogueBuilder"
          @contextmenu="onContextMenu"
        ) {{ displayText(item.primary) }}
  Spinner.spin(v-else-if="loading" size="s")
</template>

<script setup lang="ts">
import { ref, onMounted, type PropType } from 'vue'
import { useRogue } from '../../../js/rogue'
import { useRogueBuilder } from '../../../js/rogue-builder'

interface InstructionDocument {
  Sku: string
  Name: string
  Description: string
  Url: string
  IsActive: boolean
}

const props = defineProps({
  // Preferred: entries with per-item thumbnails from CMS
  entries: { type: Array as PropType<Array<{ sku?: string, isSku?: string, link?: string, thumbnail?: { src?: string, width?: number, height?: number, alt?: string } }>>, default: () => [] },
})

const rogue = useRogue()
const rogueBuilder = useRogueBuilder()

const loading = ref<boolean>(false)
const items = ref<Array<{ sku: string, primary?: InstructionDocument, thumbSrc?: string, thumbStyle?: Record<string, string>, thumbWidth?: number, thumbHeight?: number }>>([])

function onContextMenu(evt: MouseEvent) {
  // Add a temporary class to show black outline on right click
  const target = (evt.currentTarget as HTMLElement) || (evt.target as HTMLElement)
  if (!target) return

  target.classList.add('context-outline')
  // remove after short timeout so normal focus styles are not stuck
  window.setTimeout(() => target.classList.remove('context-outline'), 150)
}

function pickPrimaryInstruction(docs?: InstructionDocument[], isSku?: string): InstructionDocument | undefined {
  if (!docs || !docs.length) return undefined

  // Only consider active docs
  const active = docs.filter(d => d?.IsActive)
  if (!active.length) return undefined

  // Deduplicate by Name (API may include duplicates)
  const seen = new Set<string>()
  const unique = active.filter(d => {
    if (seen.has(d.Name)) return false
    seen.add(d.Name)

    return true
  })

  // If an IS SKU prefix was provided, find instruction where name starts with it
  if (isSku) {
    const prefix = isSku.trim().toUpperCase()
    const match = unique.find(d => {
      const docName = (d.Name || '').toUpperCase()
      // Match if the doc name starts with the IS SKU prefix

      return docName.startsWith(prefix)
    })
    if (match) return match
  }

  // Prefer customer instruction documents when available
  const customerRegex = /CUST_INSTR.+\.pdf/i
  const preferred = unique.find(d => customerRegex.test(d.Name))

  return preferred || unique[0]
}

function displayText(doc?: InstructionDocument): string {
  if (!doc) return ''
  const base = (doc.Description || '').replace(/Customer/i, '').replace(/Instructions?/i, '').trim()

  return base || doc.Sku || 'View Instructions'
}

async function fetchInstructionsForSkus(skus: string[]): Promise<Record<string, InstructionDocument[]>> {
  if (!skus.length) return {}
  const url = `/svc_content/catalog/product/instructions?sku=${encodeURIComponent(skus.join(','))}`
  const response = await rogue.fetch(url)

  return (response as Record<string, InstructionDocument[]>) || {}
}

async function loadFromEntries() {
  const validEntries = (props.entries || []).filter(e => String((e?.sku || '').trim()))

  if (!validEntries.length) {
    items.value = []
    
    return
  }

  loading.value = true
  try {
    // Get unique SKUs for the API call
    const uniqueSkus = Array.from(new Set(validEntries.map(e => String((e?.sku || '').trim()))))
    const bySku = await fetchInstructionsForSkus(uniqueSkus)

    // Process each entry separately (not deduplicated)
    const flattened: Array<{ sku: string, primary?: InstructionDocument, thumbSrc?: string, thumbStyle?: Record<string, string>, thumbWidth?: number, thumbHeight?: number }> = validEntries.map(entry => {
      const skuCode = String((entry?.sku || '').trim())
      const docs = Array.isArray((bySku as any)[skuCode]) ? (bySku as any)[skuCode] : []
      let primary = pickPrimaryInstruction(docs, entry?.isSku)

      // Fallback: if API returned no instruction document, but entry provides a link, synthesize a doc
      if (!primary && entry?.link) {
        primary = {
          Sku         : skuCode,
          Name        : 'Customer Instructions',
          Description : 'View Instructions',
          Url         : String(entry.link),
          IsActive    : true,
        }
      }

      const media = entry?.thumbnail || {}
      const thumbSrc = media?.src || primary?.Url || ''

      const style: Record<string, string> = {
        objectFit : 'contain',
        display   : 'block',
        width     : '100%',
        height    : 'auto',
      }
      const hasMediaDimensions = !!(media?.width && media?.height)
      const thumbWidth = hasMediaDimensions ? media.width : 360
      const thumbHeight = hasMediaDimensions ? media.height : 642

      return { sku: skuCode, primary, thumbSrc, thumbStyle: style, thumbWidth, thumbHeight }
    }).filter(entry => !!entry.primary)

    items.value = flattened
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  const anySkus = (props.entries || []).some(e => String((e?.sku || '').trim()))
  if (anySkus) {
    loadFromEntries()
  }
})

</script>

<style lang="scss" scoped>
.pdp-instructions {
  --above-header-height: 0px;
  --checkout-banner-height: 0px;
  --cookie-agree-height: 0px;
  --mbn-banner-height: 0px;
  font-family: "Roboto", Helvetica, Arial, sans-serif;
  font-size: 1em;
  min-width: 100%;
  box-sizing: border-box;
  overflow: hidden;
  background-color: white;
  border: white;

  // Force a single-row flex container so columns never stack
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: flex-start;
  justify-content: flex-start;
  // Match legacy row gutters with negative outer margins and per-column padding
  margin-left: -8px;
  margin-right: -8px;

  // Apply horizontal gutters to columns; width is controlled by existing grid classes (e.g., cd-6)
  .column {
    padding-left: 8px;
    padding-right: 8px;
    box-sizing: border-box;
  }

  // Match legacy media container sizing inside columns
  .media-wrap {
    max-width: 400px;
    display: inline-block;
    width: 100%;
  }

  // Match legacy media sizing per column
  .media-wrap {
    max-width: 400px;
    display: inline-block;
    width: 100%;
  }

  .thumb-link {
    display: block;
    width: 100%;
    background: transparent !important;
    border: 0 !important;
    box-shadow: none !important;
    outline: none !important;
    padding: 0 !important;
    text-decoration: none !important;
    color: inherit !important;

    &:hover,
    &:focus,
    &:active {
      background: transparent !important;
      border: 0 !important;
      box-shadow: none !important;
      outline: none !important;
      color: inherit !important;
      text-decoration: none !important;
    }

    .thumb {
      width: 100%;
      height: auto;
    }
  }

  // Show black outline briefly on right-click
  .thumb-link.context-outline,
  .instructions-link.context-outline {
    outline: 2px solid black !important;
    outline-offset: 2px;
  }

  .instructions-link {
    text-decoration: none;
  }
}

</style>


